import cuid from "cuid";
import { get, keyBy, map, omit } from "lodash";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useQuery } from "shared/hooks/useApi";
import {
  clearPpeDetails,
  resolveIsPep,
} from "screens/Private/Broker/Subscription/Detail/Templates/Acoris/Widgets/AdditionalInformation/ppeDeclaration";
import { NAME_SPACES } from "shared/locales/constants";
import { SUBSCRIPTION } from "utils/api/graphql/queries/subscription";
import {
  CASE_TYPES,
  COMMERCIALIZATION_TYPES,
  CONTACT_POINT_SYSTEM,
  KEYS,
  PROJECT_TYPES,
  SALE_MODES,
} from "utils/constants";
import Loader from "../Components/Loader";
import Navbar from "../Components/Navbar/Navbar";
import View from "./View";

const EffectiveDate = ({
  project,
  onNext,
  onBack,
  currentStatus,
  tracer,
  updateUrl,
}) => {
  const { t } = useTranslation(NAME_SPACES.PUBLIC.ACORIS);
  const TRANSLATION = t("EFFECTIVE_DATE", { returnObjects: true });
  const subscriptionId =
    get(project, "fields.currentSubscription") ||
    localStorage.getItem(KEYS.SUBSCRIPTION_ID);

  const [loading, setLoading] = useState(false);
  const { data } = useQuery(SUBSCRIPTION, {
    variables: { where: { id: subscriptionId } },
  });

  const onSubmit = ({
    contract,
    ria,
    fields,
    terminationRequest,
    contact: formContact,
  }) => {
    const source = localStorage.getItem(KEYS.SOURCE);
    updateUrl({
      subscriptionId,
      url: get(
        data,
        "subscription.insurancePlan.config.generatedUrl",
        `${process.env.REACT_APP_BASE_URL}/#/public/process/init/${source}`,
      ),
    });
    const profession = get(fields, "profession");
    const familyStatus = get(fields, "familyStatus");
    const birthCountry = get(fields, "birthCountry");
    const birthCity = get(fields, "birthCity");
    const birthDepartment = get(fields, "birthDepartment");
    const nationality = get(fields, "nationality");
    const activityStatus = get(fields, "activityStatus");
    const restFields = omit(fields || {}, [
      "isPep",
      "isPPE",
      "ppeFunction",
      "ppeStartDate",
      "ppeEndDate",
    ]);
    const isPep = resolveIsPep(get(formContact, "politicallyExposedPerson"));
    const ppeDetails =
      isPep === true
        ? {
            ppeFunction: get(formContact, "fields.ppeFunction"),
            ppeStartDate: get(formContact, "fields.ppeStartDate"),
            ppeEndDate: get(formContact, "fields.ppeEndDate") || null,
          }
        : clearPpeDetails();

    const contactFields = {
      ...get(project, "contact.fields", {}),
      ...(activityStatus && { activityStatus, status: activityStatus }),
      ...ppeDetails,
    };

    const existingTelecoms = keyBy(
      get(project, "contact.telecoms", []),
      "system",
    );
    const updatedTelecoms = { ...existingTelecoms };

    if (birthCountry) {
      updatedTelecoms[CONTACT_POINT_SYSTEM.BIRTH_COUNTRY] = {
        id: existingTelecoms[CONTACT_POINT_SYSTEM.BIRTH_COUNTRY]?.id || cuid(),
        system: CONTACT_POINT_SYSTEM.BIRTH_COUNTRY,
        value: birthCountry,
      };
    }

    if (birthCity) {
      updatedTelecoms[CONTACT_POINT_SYSTEM.BIRTH_CITY] = {
        id: existingTelecoms[CONTACT_POINT_SYSTEM.BIRTH_CITY]?.id || cuid(),
        system: CONTACT_POINT_SYSTEM.BIRTH_CITY,
        value: birthCity,
      };
    }

    if (birthDepartment) {
      updatedTelecoms[CONTACT_POINT_SYSTEM.DEPARTMENT_OF_BIRTH] = {
        id:
          existingTelecoms[CONTACT_POINT_SYSTEM.DEPARTMENT_OF_BIRTH]?.id ||
          cuid(),
        system: CONTACT_POINT_SYSTEM.DEPARTMENT_OF_BIRTH,
        value: birthDepartment,
      };
    }

    const telecomsArray = map(updatedTelecoms, (item) => item);

    return onNext({
      payload: {
        contact: {
          ...get(project, "contact"),
          ...(profession && { profession }),
          ...(familyStatus && { familySituation: familyStatus }),
          ...(nationality && { nationality }),
          politicallyExposedPerson: isPep,
          ...(Object.keys(contactFields).length > 0 && {
            fields: contactFields,
          }),
          ...(telecomsArray.length > 0 && { telecoms: telecomsArray }),
          acceptContact: true,
          acceptDigital: true,
        },
        contract: {
          ...contract,
          caseType: ria
            ? CASE_TYPES.RETURN_TO_COMPETITION
            : CASE_TYPES.NEW_CONTRACT,
          saleMode: SALE_MODES.DISTANT,
        },
        ria,
        fields: {
          ...restFields,
          commercializationType: COMMERCIALIZATION_TYPES.REMOTE,
        },
        terminationRequest,
        projectType: PROJECT_TYPES.HOT_LEAD,
      },
      onCompleted: () => setLoading(false),
    });
  };

  if (loading || !get(data, "subscription")) return <Loader />;

  return (
    <>
      <Navbar
        project={project}
        currentStatus={currentStatus}
        tracer={tracer}
        showProfile
        showCart
      />
      <View
        contract={get(project, "contract", {})}
        fields={get(project, "fields", {})}
        terminationRequest={get(project, "terminationRequest") || null}
        ria={project?.ria}
        onSubmit={onSubmit}
        onBack={onBack}
        translate={TRANSLATION}
        project={project}
      />
    </>
  );
};

export default EffectiveDate;
